import React, {
    type ChangeEvent,
    forwardRef,
    type KeyboardEvent,
    type KeyboardEventHandler,
    type PointerEvent,
    type ReactElement,
    type TextareaHTMLAttributes,
    useEffect,
    useImperativeHandle,
    useRef
} from 'react';

import classnames from 'classnames';

import useTranslation from '../../features/locale/useTranslation';
import { useId } from '../../hooks/useId';
import { FieldLabel, type FieldLabelProps } from '../FieldLabel';
import { FormControlMessage } from '../FormControlMessage';
import { Small } from '../Typography';

import useAutoSize from './useAutoSize';

import styles from './TextArea.module.scss';

export type TextAreaFocusOptions = FocusOptions & {
    cursor?: 'start' | 'end' | 'all';
};

export type ResizableTextAreaRef = {
    textArea: HTMLTextAreaElement;
};

export type TextAreaRef = HTMLTextAreaElement & {
    focus: (options?: TextAreaFocusOptions) => void;
    blur: () => void;
    resizableTextArea?: ResizableTextAreaRef;
};

export type TextAreaProps = {
    autoFocus?: boolean;
    disabled?: boolean;
    /**
     * Pass boolean to apply the error style and pass string to show a message above the field.
     */
    error?: string | boolean | null;
    id?: string;
    /**
     * A text to appear at the top of the textarea, when provided a `label` element will wrap the textarea.
     */
    label?: string;
    maxLength?: number;
    onPressEnter?: KeyboardEventHandler<HTMLTextAreaElement>;
    readOnly?: boolean;
    showCount?: boolean;
    /**
     * An informatory tooltip that is displayed when hovering over the label icon.
     *
     * If a function is passed, it will be called with the icon element as argument:
     * ```
     * <FieldLabel label="Label" tooltip={(props) => <MyCustomTooltip title="Tooltip" {...props} />
     * ```
     */
    tooltip?: FieldLabelProps['tooltip'];
    textAreaClassName?: string;
    /**
     * The number of rows to display by default.
     */
    rows?: number;
    /**
     * Adjust the height based on the content automatically.
     * An options object can be provided to autosize to specify the minimum and maximum number of lines.
     */
    autoSize?: { minRows?: number; maxRows?: number };
    onChange?: (event: ChangeEvent<HTMLTextAreaElement>) => void;
    onPointerDown?: (event: PointerEvent<HTMLTextAreaElement>) => void;
} & TextareaHTMLAttributes<HTMLTextAreaElement>;

export const TextArea = forwardRef<TextAreaRef, TextAreaProps>(
    (
        {
            autoFocus,
            autoSize,
            className,
            disabled,
            error,
            id,
            label,
            maxLength,
            onChange,
            onPressEnter,
            placeholder,
            readOnly,
            rows = 4,
            showCount,
            textAreaClassName,
            tooltip,
            value,
            ...rest
        }: TextAreaProps,
        ref
    ): ReactElement => {
        const { t } = useTranslation();
        const textareaId = useId(id);
        const tooltipId = tooltip ? `${textareaId}-tooltip` : '';
        const valueLength = value?.toString().length || 0;

        const textareaRef = useRef<HTMLTextAreaElement>(null);

        useAutoSize({ autoSize, ref: textareaRef, value });

        const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
            if (event.key === 'Enter' && onPressEnter) {
                onPressEnter(event);
            }
        };

        const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
            onChange?.(event);
        };

        useImperativeHandle(ref, () => {
            const element = textareaRef.current;
            if (!element) {
                return {} as TextAreaRef;
            }

            const handle = element as TextAreaRef;
            const originalFocus = element.focus.bind(element);

            Object.defineProperty(handle, 'focus', {
                configurable: true,
                value: (options?: TextAreaFocusOptions) => {
                    const { cursor, ...focusOptions } = options ?? {};
                    originalFocus(focusOptions);

                    if (!cursor) {
                        return;
                    }

                    const elementValueLength = element.value.length;
                    if (cursor === 'start') {
                        element.setSelectionRange(0, 0);
                    } else if (cursor === 'end') {
                        element.setSelectionRange(elementValueLength, elementValueLength);
                    } else if (cursor === 'all') {
                        element.setSelectionRange(0, elementValueLength);
                    }
                }
            });

            Object.defineProperty(handle, 'resizableTextArea', {
                configurable: true,
                value: { textArea: element }
            });

            return handle;
        }, []);

        useEffect(() => {
            if (autoFocus && textareaRef.current) {
                const textarea = textareaRef.current;
                const { length } = textarea.value;
                textarea.focus();
                textarea.setSelectionRange(length, length);
            }
        }, [autoFocus]);

        return (
            <div className={classnames(styles.container, className)}>
                {label && (
                    <FieldLabel
                        htmlFor={textareaId}
                        label={label}
                        tooltip={tooltip}
                        tooltipId={tooltipId}
                    />
                )}
                <textarea
                    ref={textareaRef}
                    aria-describedby={tooltipId}
                    className={classnames(styles.textarea, textAreaClassName, {
                        [styles.autoSize]: autoSize,
                        [styles.error]: error,
                        [styles.disabled]: disabled,
                        [styles.readOnly]: readOnly,
                        [styles.withCount]: showCount
                    })}
                    disabled={disabled}
                    id={textareaId}
                    maxLength={maxLength}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    placeholder={readOnly && !value ? t('None') : placeholder}
                    readOnly={readOnly}
                    rows={autoSize?.minRows ? autoSize.minRows : rows}
                    value={value}
                    {...rest}
                />
                {error && typeof error !== 'boolean' && <FormControlMessage message={error} />}
                {showCount && maxLength && (
                    <Small
                        className={classnames(styles.maxLengthCount, {
                            [styles.maxReached]: valueLength >= (maxLength ?? 0)
                        })}
                        color="secondary"
                    >
                        {valueLength}/{maxLength}
                    </Small>
                )}
            </div>
        );
    }
);

TextArea.displayName = 'TextArea';
